import {
  Flex,
  HStack,
  IconButton,
  Select,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { PAGE_SIZE_OPTIONS } from '@/utils/fileSorting'

export interface TablePaginationProps {
  /** 1-based. */
  page: number
  pageSize: number
  totalItems: number
  pageSizeOptions?: readonly number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

/**
 * Table footer: range summary on the left, page size and prev/next on the right.
 * Presentational — it knows nothing about files or Redux.
 */
export default function TablePagination({
  page,
  pageSize,
  totalItems,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
}: TablePaginationProps) {
  const { t, currentLanguage } = useAppTranslation('common')

  const borderColor = useColorModeValue('gray.100', 'gray.700')
  const textColor = useColorModeValue('gray.600', 'gray.400')

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalItems)

  // Thousands separators differ between en and es.
  const format = (value: number) => value.toLocaleString(currentLanguage)

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      align={{ base: 'stretch', md: 'center' }}
      justify="space-between"
      gap={3}
      px={4}
      py={3}
      borderTop="1px solid"
      borderColor={borderColor}
    >
      <Text fontSize="sm" color={textColor} aria-live="polite">
        {t('pagination.showing', {
          from: format(from),
          to: format(to),
          total: format(totalItems),
        })}
      </Text>

      <HStack spacing={{ base: 2, md: 4 }} justify={{ base: 'space-between', md: 'flex-end' }}>
        <HStack spacing={2}>
          <Text fontSize="sm" color={textColor} whiteSpace="nowrap">
            {t('pagination.rowsPerPage')}
          </Text>
          <Select
            size="sm"
            maxW="88px"
            borderRadius="md"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            aria-label={t('pagination.rowsPerPage')}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </HStack>

        <HStack spacing={1}>
          <IconButton
            size="sm"
            variant="ghost"
            icon={<FiChevronLeft />}
            aria-label={t('pagination.previousPage')}
            isDisabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          />
          <Text fontSize="sm" color={textColor} whiteSpace="nowrap" px={1}>
            {t('pagination.pageOf', { page: format(page), total: format(totalPages) })}
          </Text>
          <IconButton
            size="sm"
            variant="ghost"
            icon={<FiChevronRight />}
            aria-label={t('pagination.nextPage')}
            isDisabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          />
        </HStack>
      </HStack>
    </Flex>
  )
}
